// User settings page (v125). Two tabs:
//   SSH keys — for git-over-SSH auth
//   PATs     — personal access tokens (the existing /tokens UI inline)

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, KeyRound, Copy, Check, AlertTriangle, Terminal } from 'lucide-react';
import { Card, CardBody, CardFooter, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { cn } from '../lib/cn';
import {
  useSSHKeys,
  addSSHKey,
  deleteSSHKey,
} from '../api/me';
import {
  useTokens,
  createToken,
  revokeToken,
  type CreatedToken,
} from '../api/tokens';

const SCOPES = ['docker:pull', 'docker:push', 'read', 'write', 'admin'];

type Tab = 'ssh' | 'pat';

export function Settings() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('ssh');

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">{t('settings.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">{t('settings.title')}</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">{t('settings.intro')}</p>
      </div>

      <div className="border-b border-border flex">
        {(['ssh', 'pat'] as const).map((tabKey) => (
          <button
            key={tabKey}
            type="button"
            onClick={() => setTab(tabKey)}
            className={cn(
              'orb-tab border-b-2',
              tab === tabKey
                ? 'text-accent border-accent'
                : 'text-fg-muted border-transparent hover:text-fg',
            )}
          >
            {tabKey === 'ssh' ? t('settings.tabs.ssh') : t('settings.tabs.pat')}
          </button>
        ))}
      </div>

      {tab === 'ssh' ? <SSHKeysTab /> : <PATTab />}
    </div>
  );
}

function SSHKeysTab() {
  const { t } = useTranslation();
  const { data: keys, mutate, error } = useSSHKeys();
  const [name, setName] = useState('');
  const [pubKey, setPubKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onAdd = async () => {
    if (!name.trim() || !pubKey.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await addSSHKey({ name: name.trim(), public_key: pubKey.trim() });
      setName('');
      setPubKey('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm(t('settings.ssh.confirmDelete'))) return;
    await deleteSSHKey(id);
    await mutate();
  };

  return (
    <>
      <Card>
        <CardHeader title={t('settings.ssh.add.title')} description={t('settings.ssh.add.description')} />
        <CardBody className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('settings.ssh.add.namePlaceholder')} />
          <textarea
            value={pubKey}
            onChange={(e) => setPubKey(e.target.value)}
            placeholder={t('settings.ssh.add.keyPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg placeholder:text-fg-subtle border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-xs font-mono"
          />
          {err && <p className="text-danger font-mono text-xs">{err}</p>}
          {error && (
            <p className="text-fg-muted text-xs flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              {String(error).includes('401') ? t('settings.ssh.signInError') : String(error)}
            </p>
          )}
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button variant="primary" disabled={busy || !name.trim() || !pubKey.trim()} onClick={onAdd}>
            <Plus className="h-4 w-4" />
            {t('settings.ssh.add.submit')}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader title={t('settings.ssh.table.title', { count: keys?.length ?? 0 })} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.ssh.table.name')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.ssh.table.fingerprint')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.ssh.table.added')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.ssh.table.lastUsed')}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {keys?.length ? (
                keys.map((k) => (
                  <tr key={k.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3 text-fg flex items-center gap-2">
                      <KeyRound className="h-3.5 w-3.5 text-fg-muted" />
                      {k.name}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-fg-muted">{k.fingerprint}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {new Date(k.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onDelete(k.id)}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-fg-muted text-sm">
                    {t('settings.ssh.table.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader title={t('settings.ssh.howTo.title')} />
        <CardBody className="space-y-2 text-sm text-fg-muted">
          <p>{t('settings.ssh.howTo.generate')}</p>
          <pre className="orb-code text-xs p-3 rounded-md bg-bg-inset block">
{`ssh-keygen -t ed25519 -C "${'<your-email>'}"
cat ~/.ssh/id_ed25519.pub`}
          </pre>
          <p>{t('settings.ssh.howTo.paste')}</p>
        </CardBody>
      </Card>
    </>
  );
}

function PATTab() {
  const { t } = useTranslation();
  const { data: tokens, mutate } = useTokens();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['docker:pull']);
  const [expiresIn, setExpiresIn] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleScope = (s: string) => {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const onCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const minted = await createToken({
        name: name.trim(),
        scopes,
        expiresIn: expiresIn || undefined,
      });
      setCreated(minted);
      setName('');
      setExpiresIn('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onRevoke = async (id: string) => {
    if (!confirm(t('settings.pat.confirmRevoke'))) return;
    await revokeToken(id);
    await mutate();
  };

  return (
    <>
      {created && (
        <Card className="border-success/40 bg-success/5">
          <CardHeader title={t('settings.pat.created.title')} description={t('settings.pat.created.description')} />
          <CardBody>
            <div className="flex items-center gap-2 p-3 rounded-md bg-bg-inset border border-border font-mono text-sm break-all">
              <Terminal className="h-4 w-4 text-success shrink-0" />
              <span className="flex-1">{created.token}</span>
              <button
                type="button"
                onClick={onCopy}
                className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border hover:bg-bg-hover text-fg-muted hover:text-fg text-sm"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t('settings.pat.created.copied') : t('settings.pat.created.copy')}
              </button>
            </div>
          </CardBody>
          <CardFooter className="flex justify-end">
            <Button variant="secondary" onClick={() => setCreated(null)}>
              {t('settings.pat.created.dismiss')}
            </Button>
          </CardFooter>
        </Card>
      )}

      <Card>
        <CardHeader title={t('settings.pat.create.title')} description={t('settings.pat.create.description')} />
        <CardBody className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('settings.pat.create.namePlaceholder')} />
          <div>
            <p className="orb-section-title text-fg-muted mb-2">{t('settings.pat.create.scopesLabel')}</p>
            <div className="flex flex-wrap gap-2">
              {SCOPES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleScope(s)}
                  className={
                    'px-3 h-8 rounded-md text-xs font-mono border ' +
                    (scopes.includes(s)
                      ? 'border-accent text-accent bg-accent/10'
                      : 'border-border text-fg-muted hover:text-fg')
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <Input value={expiresIn} onChange={(e) => setExpiresIn(e.target.value)} placeholder={t('settings.pat.create.expiresPlaceholder')} />
          {err && <p className="text-danger font-mono text-sm">{err}</p>}
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button variant="primary" disabled={busy || !name.trim() || scopes.length === 0} onClick={onCreate}>
            <Plus className="h-4 w-4" />
            {t('settings.pat.create.submit')}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader title={t('settings.pat.table.title', { count: tokens?.length ?? 0 })} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.pat.table.name')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.pat.table.scopes')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.pat.table.created')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('settings.pat.table.lastUsed')}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {tokens?.length ? (
                tokens.map((tok) => (
                  <tr key={tok.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3 text-fg">{tok.name}</td>
                    <td className="px-5 py-3">
                      {tok.scopes.map((s) => (
                        <span key={s} className="orb-code text-[10px] mr-1">{s}</span>
                      ))}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">{new Date(tok.createdAt).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {tok.lastUsedAt ? new Date(tok.lastUsedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button type="button" onClick={() => onRevoke(tok.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-fg-muted text-sm">
                    {t('settings.pat.table.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
